// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Skipper tracker, checks, downloads (Phase 3) — validity checks
// ==============================================
// Two small lines on the crew item screen (Crew-4), each a one-line insertion
// so the passport form itself stays as it is:
//   • ValidityLine — as the expiry is typed or read: "Valid past 26 Jan 2027,
//     as Tony needs" in green, or the problem in red (validity.ts);
//   • ItemQuestion — on a shared paper the skipper has flagged: their question
//     ("Ask … about it"), or that it needs their look, and why.
// Both read the request from the query cache (useCrewRequest), so they need
// only the request id. Nothing under 13 px (readability.test.ts).
// ==============================================

import { useCrewRequest } from './hooks';
import { validityFor } from './validity';
import type { CrewItemView } from './types';
import { firstName } from './dates';

const Mark = ({ ok }: { ok: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0">
    {ok ? <path d="M5 12.5l4.5 4.5L19 7.5" /> : <><path d="M12 3l9.5 17h-19z" /><path d="M12 10v4" /><path d="M12 17.5v.5" /></>}
  </svg>
);

export function ValidityLine({ rid, type, expiry }: { rid: string; type: string; expiry: string | null | undefined }) {
  const q = useCrewRequest(rid);
  const v = q.data ? validityFor(type, expiry, q.data.request) : null;
  if (!v) return null;
  return (
    <div role="status" className={`flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-[15px] font-semibold ${v.ok ? 'bg-[rgba(52,210,122,0.1)] text-[#6ee7a2]' : 'bg-[rgba(255,107,107,0.12)] text-[#ffb3b3]'}`}>
      <Mark ok={v.ok} />{v.text}
    </div>
  );
}

export function ItemQuestion({ rid, item }: { rid: string; item: CrewItemView }) {
  const q = useCrewRequest(rid);
  if (item.state !== 'needs_attention' || !q.data) return null;
  const who = firstName(q.data.request.requesterName);
  const why = item.check ? validityFor(item.type, item.check.expiresOn, q.data.request) : null;
  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-[rgba(255,107,107,0.12)] px-3.5 py-3 text-[15px] leading-relaxed text-[#ffd0d0]">
      <span className="font-semibold text-[#ffb3b3]">{item.question ? `${who} asks:` : `${who} will take a look at this`}</span>
      {item.question && <span>“{item.question}”</span>}
      {why && !why.ok && <span>{why.text}</span>}
      {item.question && <span>Reply to {who}’s email, or withdraw it and share a new one.</span>}
    </div>
  );
}
